/**
 * Reconcile the studio's actors against the Three.js scene — MEMBERSHIP only.
 * Per-frame transforms are applied by `useStudioActorMotion`.
 *
 * Modelled on `demo/masters-planner/hooks/useEventSceneSync.ts` but much
 * simpler: there is no time window (every actor in the scene is always present),
 * no tugs and no fuel trucks.
 *
 * The diff signature is IDENTITY ONLY (`typeCode` + `scale`), never position —
 * so dragging an actor never removes and re-adds its model, which would
 * re-trigger a GLB clone and flicker.
 *
 * This owns every object it adds, which is why the page passes
 * `renderAllocations={false}` to `MapboxScene`: that component's own aircraft
 * diff would otherwise delete anything it doesn't recognise.
 */
import { useEffect, useRef } from 'react'
import type { MapboxCustomLayerService } from '@/services/3d'
import { ThrottledModelQueue } from '@/services/3d'
import type { StudioCatalog } from '../data/loadStudioCatalog'
import { actorToPlaced } from '../mappers/actorToPlaced'
import { restPose, sampleActorPose } from '../motion/sampleActorPose'
import { sceneIdFor, type CinematicAircraft } from '../types'

export function useStudioSceneSync(
  layerService: MapboxCustomLayerService | null,
  actors: CinematicAircraft[],
  catalog: StudioCatalog | undefined,
  /** Current scene clock — read at add time so a late insert lands at pose(t). */
  currentMsRef: React.RefObject<number>
) {
  const inScene = useRef(new Map<string, string>())
  const queueRef = useRef<ThrottledModelQueue | null>(null)
  if (!queueRef.current) queueRef.current = new ThrottledModelQueue(0, 0)

  useEffect(() => {
    if (!layerService || !catalog) return
    const queue = queueRef.current!

    const desired = new Map<string, CinematicAircraft>()
    for (const actor of actors) {
      if (!catalog.has(actor.typeCode)) continue
      desired.set(sceneIdFor(actor.id), actor)
    }

    // REMOVE actors that are gone, or whose identity changed (type/scale swap
    // needs a fresh model rather than a transform).
    for (const [id, sig] of inScene.current) {
      const actor = desired.get(id)
      const nextSig = actor ? `${actor.typeCode}:${actor.scale}` : null
      if (nextSig !== sig) {
        layerService.removeAircraft(id)
        inScene.current.delete(id)
        queue.cancel(id)
      }
    }

    // ADD what's missing, at its current animated pose.
    for (const [id, actor] of desired) {
      if (inScene.current.has(id)) continue
      const sig = `${actor.typeCode}:${actor.scale}`
      inScene.current.set(id, sig)
      queue.enqueue(id, async () => {
        const t = currentMsRef.current ?? 0
        const pose = actor.path
          ? sampleActorPose(actor.path, t, actor.position, actor.headingDeg, actor.altitudeFt)
          : restPose(actor.position, actor.headingDeg, actor.altitudeFt)
        const placed = actorToPlaced(actor, catalog, {
          position: pose.position,
          rotationDeg: pose.rotationDeg,
        })
        if (!placed) return { neededLoad: false }
        const loader = layerService.getAircraftLoader()
        const neededLoad = loader ? loader.needsNetworkLoad(placed) : false
        await layerService.addAircraft(placed)
        return { neededLoad }
      })
    }
  }, [layerService, actors, catalog, currentMsRef])

  // Full teardown on unmount so leaving the studio never leaves models behind.
  useEffect(() => {
    return () => {
      if (!layerService) return
      for (const id of inScene.current.keys()) layerService.removeAircraft(id)
      inScene.current.clear()
      queueRef.current?.clear()
    }
  }, [layerService])
}
